import { useState } from 'react';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getActiveStatCard, getInactiveStatCard } from '@/utils/activeStatCards';
import { marketProjectsInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { MarketProjectFormDialog } from '../components/MarketProjectFormDialog';
import { getMarketProjectColumns } from '../components/MpP2Columns';
import { useMarketProjectSearch } from '../hooks/useMarketProjectSearch';
import type { MarketProject, MarketProjectResponse } from '../types/marketPromotionP2.types';

function getMarketProjectStatCards(meta: MarketProjectResponse['meta'] | undefined): LabeledStat[] {
  return [
    { key: 'projects', label: 'Projects', value: meta?.total },
    getActiveStatCard(meta?.totals),
    getInactiveStatCard(meta?.totals),
    {
      key: 'beforeDeadline',
      label: 'Before Deadline',
      value: meta?.totals.beforeDeadline,
    },
  ];
}

export function MarketProjectPage() {
  const controller = useMarketProjectSearch();
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(marketProjectsInfiniteQueryOptions(controller.search));

  const [editing, setEditing] = useState<MarketProject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const columns = getMarketProjectColumns(
    {
      onEdit: (row) => {
        setEditing(row);
        setFormOpen(true);
      },
    },
    controller.search.search,
  );

  return (
    <CatalogueListLayout
      title="Market Promotion Project"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search projects',
      }}
      stats={{
        cards: getMarketProjectStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      addRequires={MARKET_PROMOTION_PERMISSIONS.PROJECT_CREATE}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Project"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the projects"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No projects found"
          {...loadMoreProps}
        />
      )}

      {isFormOpen ? (
        <MarketProjectFormDialog project={editing} onClose={() => setFormOpen(false)} />
      ) : null}
    </CatalogueListLayout>
  );
}
